/**
 * Whether the signed-in attorney has a Google or Outlook calendar connected
 * (via Composio), so the Home rail and firm calendar can offer to connect
 * one. Read-only; the Connectors page owns the connect flow itself.
 */
import { useQuery } from "@tanstack/react-query";

import { ConnectorsAPI } from "@/features/connectors/api/connectorsApi";
import { queryClient } from "@/lib/query/queryClient";

export const CALENDAR_CONNECTOR_SLUGS = ["googlecalendar", "outlook"] as const;

export type CalendarConnection = {
  /** Composio is on for this deployment. */
  enabled: boolean;
  /** At least one calendar connector is connected for this user. */
  connected: boolean;
  isLoading: boolean;
};

export const calendarConnectionKey = ["connectors", "calendar"] as const;

export const useCalendarConnection = (): CalendarConnection => {
  const query = useQuery(
    {
      queryKey: calendarConnectionKey,
      queryFn: () => ConnectorsAPI.list(),
      staleTime: 5 * 60_000,
      meta: { persist: true }
    },
    queryClient
  );
  const { data } = query;
  const connected = Boolean(
    data?.items.some(
      (c) =>
        (CALENDAR_CONNECTOR_SLUGS as readonly string[]).includes(c.slug) &&
        c.status === "connected"
    )
  );
  return {
    enabled: data?.enabled ?? true,
    connected,
    isLoading: query.isPending && !data
  };
};
